import { Fragment } from "react";
import { t } from "ttag";

import { skipToken, useListCollectionItemsQuery } from "metabase/api";
import { PERSONAL_COLLECTIONS } from "metabase/common/collections/constants";
import { EmptyState } from "metabase/common/components/EmptyState";
import { LoadingAndErrorWrapper } from "metabase/common/components/LoadingAndErrorWrapper";
import { SelectList } from "metabase/common/components/SelectList";
import { useTranslateContent } from "metabase/content-translation/hooks";
import { PLUGIN_MODERATION } from "metabase/plugins";
import { Box } from "metabase/ui";
import { getQuestionVirtualTableId } from "metabase-lib/v1/metadata/utils/saved-questions";
import type { CollectionId, DatabaseId, TableId } from "metabase-types/api";

import type { SavedEntityType } from "../types";

import SavedEntityListS from "./SavedEntityList.module.css";
import { CARD_INFO } from "./constants";

interface SavedEntityListProps {
  type: SavedEntityType;
  selectedId?: TableId;
  databaseId?: DatabaseId | null;
  collectionId?: CollectionId;
  onSelect: (tableOrModelId: string) => void;
}

export const SavedEntityList = ({
  type,
  selectedId,
  databaseId,
  collectionId,
  onSelect,
}: SavedEntityListProps): JSX.Element => {
  const tc = useTranslateContent();
  const emptyState = (
    <Box m="7.5rem 0">
      <EmptyState message={t`Nothing here`} />
    </Box>
  );

  const isVirtualCollection = collectionId === PERSONAL_COLLECTIONS.id;

  const { data, error, isFetching } = useListCollectionItemsQuery(
    collectionId != null && !isVirtualCollection
      ? {
          id: collectionId,
          models: [CARD_INFO[type].model],
          "sort-column": "name",
          "sort-direction": "asc",
        }
      : skipToken,
  );
  const list = data?.data ?? [];
  const filteredList = databaseId
    ? // When `databaseId` is provided, we're joining data, so we need to filter out items that don't belong to the current database
      list.filter((collectionItem) => collectionItem.database_id === databaseId)
    : list;

  return (
    <Box p="sm" w="100%">
      <SelectList className={SavedEntityListS.SavedEntityListRoot}>
        <LoadingAndErrorWrapper
          className={SavedEntityListS.LoadingWrapper}
          loading={collectionId == null || isFetching}
          error={error}
        >
          <Fragment>
            {filteredList.map((collectionItem) => {
              const { id, name, moderated_status } = collectionItem;
              const virtualTableId = getQuestionVirtualTableId(id);

              return (
                <SelectList.Item
                  classNames={{
                    root: SavedEntityListS.SavedEntityListItem,
                    icon: SavedEntityListS.SavedEntityListItemIcon,
                  }}
                  key={id}
                  id={id}
                  isSelected={selectedId === virtualTableId}
                  size="small"
                  name={tc(name)}
                  icon={{
                    name: CARD_INFO[type].icon,
                    size: 16,
                  }}
                  onSelect={() => onSelect(virtualTableId)}
                  rightIcon={PLUGIN_MODERATION.getStatusIcon(
                    moderated_status ?? undefined,
                  )}
                />
              );
            })}
            {filteredList.length === 0 ? emptyState : null}
          </Fragment>
          {isVirtualCollection && emptyState}
        </LoadingAndErrorWrapper>
      </SelectList>
    </Box>
  );
};
